import type { EmblaCarouselType } from 'embla-carousel'

export const addThumbBtnsClickHandlers = (
	emblaApiMain: EmblaCarouselType,
	emblaApiThumb: EmblaCarouselType
): (() => void) => {
	const slidesThumbs = emblaApiThumb.slideNodes()
	const autoplay = emblaApiMain?.plugins()?.autoplay

	const scrollToIndex = slidesThumbs.map((_, index) => (): void => {
		emblaApiMain.scrollTo(index)
		if (autoplay) {
			autoplay.stop()
		}
	})

	slidesThumbs.forEach((slideNode, index) => {
		slideNode.addEventListener('click', scrollToIndex[index], false)
	})

	return (): void => {
		slidesThumbs.forEach((slideNode, index) => {
			slideNode.removeEventListener('click', scrollToIndex[index], false)
		})
	}
}

export const addToggleThumbBtnsActive = (
	emblaApiMain: EmblaCarouselType,
	emblaApiThumb: EmblaCarouselType
): (() => void) => {
	const slidesThumbs = emblaApiThumb.slideNodes()

	const toggleThumbBtnsState = (): void => {
		emblaApiThumb.scrollTo(emblaApiMain.selectedScrollSnap())
		const previous = emblaApiMain.previousScrollSnap()
		const selected = emblaApiMain.selectedScrollSnap()
		slidesThumbs[previous].classList.remove('embla-thumbs__slide--selected')
		slidesThumbs[selected].classList.add('embla-thumbs__slide--selected')
	}

	emblaApiMain.on('select', toggleThumbBtnsState)
	emblaApiThumb.on('init', toggleThumbBtnsState)

	return (): void => {
		const selected = emblaApiMain.selectedScrollSnap()
		slidesThumbs[selected].classList.remove('embla-thumbs__slide--selected')
	}
}

export const addDotBtnsAndClickHandlers = (
	emblaApi: EmblaCarouselType,
	dotsNode: HTMLElement
): (() => void) => {
	let dotNodes: HTMLElement[] = []

	const addDotBtnsWithClickHandlers = (): void => {
		dotsNode.innerHTML = emblaApi
			.scrollSnapList()
			.map(
				(_, index) =>
					`<button class="embla__dot semantic-button" type="button">
						<span class="visually-hidden">Go to page ${index}</span>
						</button>`
			)
			.join('')

		const scrollTo = (index: number): void => {
			emblaApi.scrollTo(index)
		}

		dotNodes = Array.from(dotsNode.querySelectorAll('.embla__dot'))
		dotNodes.forEach((dotNode, index) => {
			dotNode.addEventListener('click', () => scrollTo(index), false)
		})
	}

	const toggleDotBtnsActive = (): void => {
		const previous = emblaApi.previousScrollSnap()
		const selected = emblaApi.selectedScrollSnap()
		dotNodes[previous].classList.remove('embla__dot--selected')
		dotNodes[selected].classList.add('embla__dot--selected')
	}

	emblaApi
		.on('init', addDotBtnsWithClickHandlers)
		.on('reInit', addDotBtnsWithClickHandlers)
		.on('init', toggleDotBtnsActive)
		.on('reInit', toggleDotBtnsActive)
		.on('select', toggleDotBtnsActive)

	return (): void => {
		dotsNode.innerHTML = ''
	}
}

export const addTogglePrevNextBtnsActive = (
	emblaApi: EmblaCarouselType,
	prevBtn: HTMLElement,
	nextBtn: HTMLElement
): (() => void) => {
	const togglePrevNextBtnsState = (): void => {
		if (emblaApi.canScrollPrev()) prevBtn.removeAttribute('disabled')
		else prevBtn.setAttribute('disabled', 'disabled')

		if (emblaApi.canScrollNext()) nextBtn.removeAttribute('disabled')
		else nextBtn.setAttribute('disabled', 'disabled')
	}

	emblaApi
		.on('select', togglePrevNextBtnsState)
		.on('init', togglePrevNextBtnsState)
		.on('reInit', togglePrevNextBtnsState)

	return (): void => {
		prevBtn.removeAttribute('disabled')
		nextBtn.removeAttribute('disabled')
	}
}

export const addPrevNextBtnsClickHandlers = (
	emblaApi: EmblaCarouselType,
	prevBtn: HTMLElement,
	nextBtn: HTMLElement
): (() => void) => {
	const scrollPrev = (): void => {
		emblaApi.scrollPrev()
	}
	const scrollNext = (): void => {
		emblaApi.scrollNext()
	}
	prevBtn.addEventListener('click', scrollPrev, false)
	nextBtn.addEventListener('click', scrollNext, false)

	const removeTogglePrevNextBtnsActive = addTogglePrevNextBtnsActive(
		emblaApi,
		prevBtn,
		nextBtn
	)

	return (): void => {
		removeTogglePrevNextBtnsActive()
		prevBtn.removeEventListener('click', scrollPrev, false)
		nextBtn.removeEventListener('click', scrollNext, false)
	}
}
